import { FieldValues } from 'react-hook-form';
import { TableWithPaginationProps } from './props';
import styles from './styles.module.scss';
import { ErrorCard } from '../ErrorCard';
import { PaginationComponent } from '../PaginationComponent';
import { TableGrid } from '@starsoft/common/components';

/**
 * TableWithPagination Component
 * Renders a table with pagination controls, handling loading states, errors, and data display.
 * Combines TableGrid for data display and PaginationComponent for navigation.
 *
 * @component
 * @template T - Type extending FieldValues for row data structure
 * @param {Object} props - Component props
 * @param {Array} props.columns - Column definitions for the table
 * @param {Object} props.data - Data object containing rows and pagination metadata
 * @param {Error} props.error - Error object if request failed
 * @param {Function} props.fetchNextPage - Callback to fetch next page
 * @param {Function} props.fetchPreviousPage - Callback to fetch previous page
 * @param {boolean} props.isLoading - Loading state indicator
 * @param {number} props.page - Current page number
 * @param {Function} props.refetch - Callback to retry failed request
 * @param {boolean} props.isSecondaryStyles - Whether to use secondary styling
 * @param {Function} props.setCurrentPage - Callback to set current page number
 * @returns {JSX.Element} The rendered table with pagination
 *
 * @example
 * interface UserData extends FieldValues {
 *   id: number;
 *   name: string;
 *   email: string;
 * }
 *
 * const columns = [
 *   { field: 'name', headerName: 'Name' },
 *   { field: 'email', headerName: 'Email' }
 * ];
 *
 * const data = {
 *   data: [
 *     { id: 1, name: 'John', email: 'john@example.com' },
 *     { id: 2, name: 'Jane', email: 'jane@example.com' }
 *   ],
 *   metadata: {
 *     hasNextPage: true,
 *     hasPreviousPage: false,
 *     pageCount: 5
 *   }
 * };
 *
 * <TableWithPagination<UserData>
 *   columns={columns}
 *   data={data}
 *   error={null}
 *   fetchNextPage={() => {}}
 *   fetchPreviousPage={() => {}}
 *   isLoading={false}
 *   page={1}
 *   refetch={() => {}}
 *   isSecondaryStyles={false}
 *   setCurrentPage={(page: number) => {}}
 * />
 */
export function TableWithPagination<T extends FieldValues>({
  columns,
  data,
  error,
  fetchNextPage,
  fetchPreviousPage,
  isLoading,
  page,
  refetch,
  isSecondaryStyles,
  setCurrentPage,
  ...props
}: TableWithPaginationProps<T>) {
  if (error) {
    return <ErrorCard error={error} refetch={refetch} />;
  }

  return (
    <div className={styles.container}>
      <TableGrid
        rows={data?.data ?? []}
        columns={columns}
        isLoading={isLoading}
        isSecondaryStyles={isSecondaryStyles}
        {...props}
      />

      <PaginationComponent
        currentPage={page}
        hasPreviousPage={data?.metadata?.hasPreviousPage ?? false}
        hasNextPage={data?.metadata?.hasNextPage ?? false}
        loading={isLoading}
        lastPage={data?.metadata?.pageCount ?? 0}
        setCurrentPage={setCurrentPage}
        fetchNextPage={fetchNextPage}
        fetchPreviousPage={fetchPreviousPage}
        maxLength={7}
        secondaryStyles
      />
    </div>
  );
}
